import { useHistory, useRouteMatch } from "react-router";
import { getDefaultPath, getLoginPath } from "../utils/checkPricePathUtils";

import { useStepRouteRedirect } from "Store/steps/stepsHooks";
import { useStepStatusSelector } from "Store/steps/stepsSelectors";
import { activateStartStep } from "Store/steps/stepsSlice";
import { findStep } from "Store/steps/stepsUtils";
import { isDefined } from "Utils/functions";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHasBeenIdentified } from "./checkPriceHooks";

export const useCheckPriceRouteRedirects = (
  productPath: string,
  shouldSkipLoginStep: boolean,
): void => {
  useDefaultRouteRedirect(productPath, shouldSkipLoginStep);
  useLoginRouteRedirect(productPath, shouldSkipLoginStep);
  useStepRouteRedirect();
};

const useDefaultRouteRedirect = (
  productPath: string,
  shouldSkipLoginStep: boolean,
): void => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { activeStepId, startStepId, steps } = useStepStatusSelector();
  const hasBeenIdentified = useHasBeenIdentified();
  const defaultRouteMatch = useRouteMatch(getDefaultPath(productPath));

  useEffect(() => {
    const hasActiveStep = isDefined(findStep(steps, activeStepId));
    const hasStartStep = isDefined(findStep(steps, startStepId));
    const isOnDefaultRoute =
      isDefined(defaultRouteMatch) && defaultRouteMatch.isExact;

    if (!hasActiveStep && hasStartStep && isOnDefaultRoute) {
      if (hasBeenIdentified && shouldSkipLoginStep) {
        dispatch(activateStartStep());
      } else {
        history.replace({
          pathname: getLoginPath(productPath),
          search: history.location.search,
        });
      }
    }
  }, [steps, activeStepId, startStepId, history.location.pathname]);
};

const useLoginRouteRedirect = (
  productPath: string,
  shouldSkipLoginStep: boolean,
): void => {
  const dispatch = useDispatch();
  const { activeStepId, startStepId, steps } = useStepStatusSelector();
  const hasBeenIdentified = useHasBeenIdentified();
  const loginRouteMatch = useRouteMatch(getLoginPath(productPath));

  useEffect(() => {
    const hasActiveStep = isDefined(findStep(steps, activeStepId));
    const hasStartStep = isDefined(findStep(steps, startStepId));
    const isOnLoginRoute =
      isDefined(loginRouteMatch) && loginRouteMatch.isExact;

    if (
      !hasActiveStep &&
      hasStartStep &&
      isOnLoginRoute &&
      hasBeenIdentified &&
      shouldSkipLoginStep
    ) {
      dispatch(activateStartStep());
    }
  }, [
    steps,
    activeStepId,
    startStepId,
    hasBeenIdentified,
    loginRouteMatch?.isExact,
  ]);
};
